import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  apiService,
  FeatureFlagNames,
  userDB,
  VersionType,
} from '@taleemabad/db';

class CapacitorService {
  private latestDownloadedBundleVersion: string = '';
  private bundleVersion: string = '';
  liveUpdateStatus: string = '';
  private eventTarget = new EventTarget();

  constructor() {
    // Adding event listeners for the CapacitorUpdater
    CapacitorUpdater.addListener('download', (event: any) => {
      this.liveUpdateStatus = 'Downloading'; // Updating the live update status when a download starts
      this.dispatchStatusChange(this.liveUpdateStatus); // Dispatch status change event
    });
    CapacitorUpdater.addListener('downloadComplete', async (event: any) => {
      await this.setVersionDownloaded(this.latestDownloadedBundleVersion); // Setting the version when a download is complete
    });
  }

  // Method to update the app if there is a new version available
  async capacitorLiveUpdate() {
    try {
      const isLiveUpdateEnabled = await userDB.getFeatureFlag(
        FeatureFlagNames.liveUpdateEnabled,
      );
      // Checking if the platform is native
      if (Capacitor.isNativePlatform() && isLiveUpdateEnabled) {
        // Ensure download occurs while the app is active, or download may fail
        const { data } = await apiService.get('api/v1/frontend-bundle/', {
          headers: { api_key: process.env.NX_PUBLIC_LIVE_UPDATE_API_KEY },
        });

        // Finding the latest version from the response
        const result = data.reduce((a: any, b: any) =>
          a.version > b.version ? a : b,
        );

        // Fetching the downloaded version
        const downloadedVersion = await this.getVersionValue(
          VersionType.Downloaded,
        );

        // If the downloaded version is less than the latest version, download the latest version
        if (Number(downloadedVersion.value) < result.version) {
          await this.capacitorDownloadBuild(result);
        }
      }
    } catch (err) {}
  }

  // Method to download a build
  async capacitorDownloadBuild(build: any) {
    try {
      // Updating the latest downloaded bundle version
      this.latestDownloadedBundleVersion = build.version.toString();

      // Downloading the build
      await CapacitorUpdater.download({
        url: build.signedBundleUrl,
        version: build.version.toString(),
      });
    } catch (err: any) {}
  }

  // Method to set the downloaded version
  async setVersionDownloaded(version: string) {
    // Setting the version in the preferences
    await Preferences.set({
      key: VersionType.Downloaded,
      value: version,
    });
    // Updating the live update status
    this.liveUpdateStatus = 'Downloaded';
    this.dispatchStatusChange(this.liveUpdateStatus); // Dispatch status change event
  }

  // Method to set the installed version
  async setVersionInstalled(version: string) {
    // Setting the installed version in the preferences
    await Preferences.set({
      key: VersionType.Installed,
      value: version,
    });
  }

  // Method to get a version value
  async getVersionValue(key: string) {
    // Fetching the version from the preferences
    const version = await Preferences.get({ key });
    return version;
  }

  // Method to get all downloaded bundles
  async getAllDownloadedBundle(version: string) {
    // Listing all downloaded bundles
    const bundleInfo = await CapacitorUpdater.list();
    // Finding the bundle with the specified version
    return bundleInfo.bundles.find((data: any) => data.version === version);
  }

  // Method to install the application on load
  public async installApplicationOnLoad() {
    try {
      // Fetching the installed version
      const installedVersion = await this.getVersionValue(
        VersionType.Installed,
      );

      // Fetching the downloaded version
      const downloadedVersion = await this.getVersionValue(
        VersionType.Downloaded,
      );

      // If there is an installed version, update the bundle version
      if (installedVersion.value !== null) {
        this.bundleVersion = installedVersion.value;
      }

      // If there is a downloaded version
      if (downloadedVersion.value !== null) {
        // Fetching the bundle with the downloaded version
        const data = await this.getAllDownloadedBundle(downloadedVersion.value);

        // If the bundle exists and the installed version is less than the downloaded version
        if (data !== undefined) {
          if (
            installedVersion.value === null ||
            Number(installedVersion.value) < Number(downloadedVersion.value)
          ) {
            // Set the installed version and update the bundle
            await CapacitorUpdater.set(data);
            await this.setVersionInstalled(downloadedVersion.value);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  private dispatchStatusChange(status: string) {
    const event = new CustomEvent('statusChange', { detail: status });
    this.eventTarget.dispatchEvent(event);
  }

  public onStatusChange(callback: (status: string) => void) {
    this.eventTarget.addEventListener('statusChange', (event: Event) => {
      callback((event as CustomEvent).detail);
    });
  }

  public removeStatusListener(callback: (status: string) => void) {
    this.eventTarget.removeEventListener('statusChange', (event: Event) => {
      callback((event as CustomEvent).detail);
    });
  }
}

// Exporting an instance of the CapacitorService class
export const capacitorService = new CapacitorService();
